import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toJS } from 'mobx';

import { checkIsProfessionalUser } from './helper/ProfilePage';
import makeCompany from './helper/makeCompany';
import {
  getDescriptionDeleteAccountSuccessorModal,
  getNewCompanyAdminSuccessfulUpdatedMessage,
} from '../../utils/helpers';
import { successAlert } from '../../utils/alerts';
import { useStore } from '../../store';
import ContentContainer from '../../components/ContentContainer';
import AvatarHeader from '../../components/AvatarHeader';
import UserAvatar from '../../components/UserAvatar';
import ContactDetailsForm from './components/ContactDetailsForm';
import { addQueryParamTimeToEscapeCash } from './helper/addQueryParamTimeToEscapeCash';
import Spinner from '../../components/Spinner';
import './styles.scss';
import PageTitle from '../../components/PageTitle';
import LoginInformationForm from './components/LoginInformationForm';
import VerifyEmailModal from '../../components/Modal/VerifyEmailModal';
import VerifyPhoneModal from '../../components/Modal/VerifyPhoneModal';
import UploadAvatarModal from '../../components/Modal/UploadAvatarModal';
import SelectSuccessorModal from '../../components/Modal/SelectSuccessorModal';
import DropdownMenu from '../../components/DropdownMenu';
import withUser from '../../hoc/withUser';

const ProfilePage = observer(({ user, authToken }) => {
  const {
    rootStore: { profileStore },
  } = useStore();

  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [updatedImage, setUpdatedImage] = useState(null);
  const [initialRotation, setInitialRotation] = useState(0);
  const [selectedSuccessorId, setSelectedSuccessorId] = useState(null);
  const [selectedSuccessorName, setSelectedSuccessorName] = useState('');
  const [contactPageLoading, setContactPageLoading] = useState(true);
  const [companyData, setCompanyData] = useState({});

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const loadInitialData = async () => {
      if (!profileStore.profileData) {
        await profileStore.initProfile(user, authToken, i18n);
      }

      await profileStore.loadUser();

      profileStore.contactStore.shouldDisplaySelectSuccessorModal = false;
      if (checkIsProfessionalUser(profileStore.connection)) {
        const company = toJS(profileStore.company);

        const companyData = company ? await loadCompanyData(company.uid) : {};

        const companyLabel = makeCompany(companyData.data);

        setCompanyData(companyLabel);
      }

      setContactPageLoading(false);
    };
    loadInitialData();
    // eslint-disable-next-line
  }, []);

  const loadCompanyData = async companyUid => {
    return await profileStore.loadCompanyData(companyUid);
  };

  const togglePhoneModal = () => {
    setPhoneModalOpen(prev => !prev);
    setEmailModalOpen(false);
    setAvatarModalOpen(false);
  };

  const toggleAvatarModal = image => {
    const rotation = profileStore.extractExifRotation(image);

    setAvatarModalOpen(prev => !prev);
    setPhoneModalOpen(false);
    setEmailModalOpen(false);
    setUpdatedImage(image);
    setInitialRotation(rotation);
  };

  const toggleEmailModal = () => {
    setAvatarModalOpen(false);
    setPhoneModalOpen(false);
    setEmailModalOpen(prev => !prev);
  };

  const onNewSuccessorSelect = (successorId, successorName) => {
    setSelectedSuccessorId(successorId);
    setSelectedSuccessorName(successorName);
  };

  const onConfirmSelectSuccessor = () => {
    profileStore.contactStore.assignUserAsCompanyAdmin(selectedSuccessorId).then(() => {
      const messageAssigneeUpdateSuccess = getNewCompanyAdminSuccessfulUpdatedMessage(
        t('contact.newCompanyAdminUpdateSuccess'),
        selectedSuccessorName,
        profileStore.company?.name
      );
      profileStore.saveProfile(profileStore.profileDataToSave, t);
      successAlert(messageAssigneeUpdateSuccess);
    });
  };

  const handleSubmitContactForm = async values => {
    await profileStore.saveProfile(values, t);
    profileStore.profileDataToSave = values;
  };

  const handleCloseSuccessorModal = () => {
    profileStore.contactStore.shouldDisplaySelectSuccessorModal = false;
    setSelectedSuccessorId(null);
    setSelectedSuccessorName('');
  };
  const srcImage = profileStore?.profileData?.picture || user.picture;

  if (!profileStore.profileData) {
    return null;
  }

  return (
    <>
      <ContentContainer fullWidth={true}>
        <AvatarHeader title={'contact.title'} subtitle={t('profile.subtitle')}>
          <UserAvatar t={t} src={addQueryParamTimeToEscapeCash(srcImage)} toggleAvatar={toggleAvatarModal} />
        </AvatarHeader>
        {contactPageLoading ? (
          <Spinner pageLoading />
        ) : (
          <div className="profile-form-wrapper">
            <div className="profile-contact-div">
              <div className="profile-title-container">
                <PageTitle titleKey={t('profile.title')} />
              </div>
              <ContactDetailsForm companyData={companyData} handleSubmit={handleSubmitContactForm} />
            </div>

            <div>
              <div className="profile-div">
                <div className="profile-title-container">
                  <PageTitle titleKey={t('profile.login')} />
                </div>
                <LoginInformationForm
                  toggleAvatar={toggleAvatarModal}
                  togglePhone={togglePhoneModal}
                  toggleEmail={toggleEmailModal}
                />

                <VerifyEmailModal isOpen={emailModalOpen} toggle={toggleEmailModal} />
                <VerifyPhoneModal isOpen={phoneModalOpen} toggle={togglePhoneModal} />
                <UploadAvatarModal
                  isOpen={avatarModalOpen}
                  toggle={toggleAvatarModal}
                  image={updatedImage}
                  initialRotation={initialRotation}
                />
                <SelectSuccessorModal
                  isOpen={profileStore.contactStore.shouldDisplaySelectSuccessorModal}
                  modalTitle={t('selectSuccessorModal.switchCompany.modalTitle')}
                  title={t('selectSuccessorModal.switchCompany.title')}
                  description={getDescriptionDeleteAccountSuccessorModal(
                    t('selectSuccessorModal.description'),
                    profileStore.company?.name
                  )}
                  onClose={handleCloseSuccessorModal}
                  onConfirm={onConfirmSelectSuccessor}
                  dropdownDescription={t('selectSuccessorModal.dropdownDescription')}
                  primaryButtonTitle={t('deleteConfirmModal.deleteButton')}
                  isItemSelected={!!selectedSuccessorId}
                  validationMessage={t('selectSuccessorModal.switchCompany.validationMessage')}
                >
                  <DropdownMenu
                    options={toJS(profileStore.contactStore.listProfessionalUsers)}
                    onSelect={onNewSuccessorSelect}
                  />
                </SelectSuccessorModal>
              </div>
            </div>
          </div>
        )}
      </ContentContainer>
    </>
  );
});

export default withUser(ProfilePage);
